import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Home, Share2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RESTAURANT_PHONE = '919444363303';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsappSent, setWhatsappSent] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order details');
      setLoading(false);
    }
  };

  const sendToRestaurant = () => {
    if (!order) return;
    
    let message = `🍽️ *NEW ORDER - SPICIFI*\n\n`;
    message += `📋 *Order ID:* ${order.id.substring(0, 8).toUpperCase()}\n`;
    message += `📦 *Type:* ${order.order_type.toUpperCase()}\n\n`;
    message += `👤 *Customer Details:*\n`;
    message += `• Name: ${order.customer_name}\n`;
    message += `• Phone: ${order.customer_phone}\n`;
    if (order.customer_address) message += `• Address: ${order.customer_address}\n`;
    if (order.delivery_instructions) message += `• Instructions: ${order.delivery_instructions}\n`;
    message += `\n🛒 *Order Items:*\n`;
    order.items.forEach((item, i) => {
      message += `${i + 1}. ${item.name} x${item.quantity} - ₹${item.price * item.quantity}\n`;
    });
    message += `\n💰 *Payment Details:*\n`;
    message += `• Subtotal: ₹${order.subtotal}\n`;
    message += `• Packaging: ₹${order.packaging_charges}\n`;
    message += `• *Total: ₹${order.total_amount}*\n`;
    message += `• Payment: ${order.payment_mode === 'upi' ? 'UPI (Paid)' : 'Cash on ' + (order.order_type === 'delivery' ? 'Delivery' : 'Pickup')}\n\n`;
    message += `Please confirm this order! 🙏`;
    
    const whatsappUrl = `https://wa.me/${RESTAURANT_PHONE}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setWhatsappSent(true);
    toast.success('Opening WhatsApp to send order to restaurant...');
  };

  const shareOnWhatsApp = () => {
    if (!order) return;
    let message = `🍽️ *My Order from Spicifi*\n\n`;
    message += `*Order ID:* ${order.id.substring(0, 8)}\n`;
    message += `*Type:* ${order.order_type.toUpperCase()}\n`;
    message += `*Total:* ₹${order.total_amount}\n\n`;
    message += `Order placed successfully! 🎉`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <Card className="p-8 text-center bg-gray-800">
          <p className="text-amber-200">Order not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="p-8 text-center space-y-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
          <div className="flex justify-center">
            <div className="bg-green-500 rounded-full p-4">
              <CheckCircle className="w-16 h-16 text-white" />
            </div>
          </div>
          
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-amber-400 font-['Playfair_Display']">
              Order Placed Successfully!
            </h1>
            <p className="text-amber-200">Thank you for your order</p>
            <p className="text-sm text-gray-400">Order ID: {order.id.substring(0, 8).toUpperCase()}</p>
            {order.petpooja_order_id && (
              <p className="text-xs text-green-400">PetPooja ID: {order.petpooja_order_id}</p>
            )}
          </div>

          <Card className="p-6 bg-gray-700/50 border-amber-500/30 text-left">
            <h2 className="font-bold text-lg text-amber-400 mb-4">Order Details</h2>
            <div className="space-y-2 text-sm text-amber-100">
              <div className="flex justify-between">
                <span>Type:</span>
                <span className="font-semibold uppercase">{order.order_type}</span>
              </div>
              <div className="flex justify-between">
                <span>Customer:</span>
                <span className="font-semibold">{order.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span>Phone:</span>
                <span className="font-semibold">{order.customer_phone}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gray-700/50 border-amber-500/30 text-left">
            <h2 className="font-bold text-lg text-amber-400 mb-4">Items Ordered</h2>
            <div className="space-y-2">
              {order.items.map((item, i) => (
                <div key={i} className="flex justify-between text-sm text-amber-100">
                  <span>{item.name} x {item.quantity}</span>
                  <span>₹{item.price * item.quantity}</span>
                </div>
              ))}
              <div className="border-t-2 border-amber-500/30 pt-2 mt-2 space-y-1">
                <div className="flex justify-between text-amber-200">
                  <span>Subtotal</span>
                  <span>₹{order.subtotal}</span>
                </div>
                <div className="flex justify-between text-amber-200">
                  <span>Packaging</span>
                  <span>₹{order.packaging_charges}</span>
                </div>
                <div className="flex justify-between font-bold text-lg text-amber-400">
                  <span>Total</span>
                  <span>₹{order.total_amount}</span>
                </div>
              </div>
            </div>
          </Card>

          <div className="space-y-3 pt-4">
            {!whatsappSent ? (
              <Button onClick={sendToRestaurant} className="w-full bg-green-500 hover:bg-green-600 font-semibold py-6 text-lg animate-pulse">
                <MessageCircle className="w-5 h-5 mr-2" />
                📱 SEND ORDER TO RESTAURANT (Required)
              </Button>
            ) : (
              <Button onClick={sendToRestaurant} className="w-full bg-green-600 hover:bg-green-700 font-semibold py-6 text-lg">
                <MessageCircle className="w-5 h-5 mr-2" />
                ✓ Resend to Restaurant
              </Button>
            )}
            <p className="text-center text-amber-200 text-sm">
              👆 Click above to notify restaurant via WhatsApp
            </p>
            <Button onClick={shareOnWhatsApp} variant="outline" className="w-full border-2 border-green-500/50 hover:bg-green-500/10 text-green-400 font-semibold py-4">
              <Share2 className="w-5 h-5 mr-2" />
              Share My Order
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full border-2 border-amber-500/30 hover:bg-amber-500/10 text-amber-400 font-semibold py-6 text-lg">
              <Home className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>

          <div className="pt-4 border-t-2 border-amber-500/30">
            <p className="text-sm text-amber-200">Payment: <span className="font-semibold text-green-400">{order.payment_mode === 'upi' ? 'UPI (Paid)' : `Cash on ${order.order_type === 'delivery' ? 'Delivery' : 'Pickup'}`}</span></p>
            <p className="text-xs text-gray-400 mt-2">We'll contact you shortly to confirm your order</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
